import { Component, Input, OnInit } from '@angular/core'
import { AuthenticationService } from '../../../services/authentication.service'
import { Auth } from '@angular/fire/auth'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { Client } from '@parkupp/core'
import { ClientService } from '../../../services/client.service'
import { ModernAnalyticsService } from '../../../services/modern-analytics.service'
import { GlobalService } from '../../../services/global.service'
import { RecaptchaService } from '../../../services/recaptcha.service'

@Component({
    selector: 'app-authenticator',
    templateUrl: './authenticator.component.html',
    styleUrls: ['./authenticator.component.scss'],
})
export class AuthenticatorComponent extends FormBuilder implements OnInit {
    @Input() showAsRegistration = false
    @Input() heroSectionHidden = false

    success: (auth: Auth) => void
    failure: (auth: Auth) => void

    returnUrl: string | null
    error: string | null
    loading: boolean = false
    withPassword = false
    magicLinkMode = false
    magicLinkSent = false
    formGroup: FormGroup
    magicLinkForm: FormGroup
    valid: boolean = true

    constructor(
        public authService: AuthenticationService,
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private router: Router,
        private clientService: ClientService,
        public globalService: GlobalService,
        private modernAnalyticsService: ModernAnalyticsService,
        private recaptchaService: RecaptchaService
    ) {
        super()

        this.activatedRoute.queryParams.forEach((queryParams) => {
            if (queryParams['returnUrl']) {
                this.returnUrl = queryParams['returnUrl']
            }
        })

        this.formGroup = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', [Validators.required]],
        })

        this.magicLinkForm = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
        })

        this.success = (auth) => {
            this.loading = false
            this.onSignIn()
        }

        this.success = (auth) => {
            this.authService.user.reload().then(() => {
                // if (!this.authService.auth.currentUser?.emailVerified) {
                //     this.authService.auth.currentUser?.getIdToken().then((idToken) => {
                //         this.authService.sendEmailVerification(idToken).subscribe(() => {
                //             this.loading = false
                //             this.onSignIn()
                //         })
                //     })
                // }
                this.loading = false
                this.onSignIn()
            })
        }

        this.failure = (error: any) => {
            if (error.code === 'auth/user-not-found' && this.withPassword) {
                this.registerWithRecaptcha()
            } else {
                this.loading = false
                this.error = error.message
            }
        }
    }

    ngOnInit(): void {}

    onSignIn() {
        const clientSubscription = this.clientService.subscribeToClient(this.authService.uid).subscribe((document) => {
            const client = document as Client
            if (this.clientService.requiresProfileCompletion(client)) {
                if (this.returnUrl) {
                    this.router.navigate([`client/create`], { queryParams: { returnUrl: this.returnUrl } })
                } else {
                    this.router.navigate([`client/create`])
                }
            } else {
                if (this.returnUrl) {
                    this.router.navigate([this.returnUrl])
                } else {
                    this.router.navigate([`client/${this.authService.uid}`])
                }
            }
            clientSubscription.unsubscribe()
            this.loading = false
        })
    }

    signUpWithGoogle(): void {
        this.loading = true
        this.authService
            .signInWithGoogle()
            .then((result) => {
                // Track Google signup
                void this.modernAnalyticsService.trackEnhancedLead(result.user?.email)
                this.modernAnalyticsService.trackEvent('sign_up', {
                    method: 'google',
                    user_id: result.user?.uid,
                })
                this.success(result)
            })
            .catch(this.failure)
    }

    signUpWithFacebook(): void {
        this.loading = true
        this.authService
            .signInWithFacebook()
            .then((result) => {
                // Track Facebook signup
                void this.modernAnalyticsService.trackEnhancedLead(result.user?.email)
                this.modernAnalyticsService.trackEvent('sign_up', {
                    method: 'facebook',
                    user_id: result.user?.uid,
                })
                this.success(result)
            })
            .catch(this.failure)
    }

    setSignUpWithPassword(password: boolean): void {
        this.withPassword = password
        if (password) {
            this.magicLinkMode = false
        }
    }

    setMagicLinkMode(on: boolean): void {
        this.magicLinkMode = on
        if (on) {
            this.withPassword = false
            this.magicLinkSent = false
            this.error = null
        }
    }

    sendMagicLink(): void {
        if (!this.magicLinkForm.valid) {
            this.valid = false
            return
        }
        const email: string = (this.magicLinkForm.value.email || '').trim().toLowerCase()
        if (!email) {
            this.valid = false
            return
        }

        this.loading = true
        this.error = null

        const enterpriseKey = this.globalService.enterpriseKey
        this.authService.sendMagicLink(email, this.returnUrl || undefined, enterpriseKey).subscribe({
            next: () => {
                try {
                    window.localStorage.setItem('magicLinkEmail', email)
                } catch (e) {
                    console.warn('Could not persist magic link email to localStorage', e)
                }
                this.modernAnalyticsService.trackEvent('magic_link_requested', { method: 'magic_link' })
                this.loading = false
                this.magicLinkSent = true
            },
            error: (err) => {
                this.loading = false
                // Even on error we show the generic success message to avoid enumerating accounts.
                console.error('sendMagicLink error', err)
                this.magicLinkSent = true
            },
        })
    }

    signUpWithPassword(): void {
        if (this.formGroup.valid) {
            this.loading = true
            this.authService.signInWithPassword(this.formGroup.value.email, this.formGroup.value.password).then(this.success).catch(this.failure)
        } else {
            this.valid = false
        }
    }

    private registerWithRecaptcha(): void {
        this.recaptchaService
            .execute('register')
            .then((token) => this.authService.createUserWithRecaptcha(this.formGroup.value.email, this.formGroup.value.password, token))
            .then((result) => {
                void this.modernAnalyticsService.trackEnhancedLead(result.user?.email)
                this.modernAnalyticsService.trackEvent('sign_up', {
                    method: 'email',
                    user_id: result.user?.uid,
                })
                this.success(result)
            })
            .catch((error) => {
                this.loading = false
                this.error = this.registrationErrorMessage(error)
            })
    }

    private registrationErrorMessage(error: any): string {
        const code = error?.code || ''
        if (code === 'functions/already-exists' || code === 'already-exists') {
            return 'An account with this email already exists. Try logging in.'
        }
        if (code === 'functions/permission-denied' || code === 'permission-denied') {
            return "We couldn't verify this request. Please try again."
        }
        return error?.message || 'Unable to create account. Please try again.'
    }

    forgotPassword() {
        this.router.navigate(['/forgot-password'])
    }
}
